import { useRef, useState } from "react";
import { Dropdown, TextPad, type DropdownId } from "../../components/shared";
import { blurOtherField } from "../../components/shared/padFocus";
import type { FilterOpt, CancelSort, CancelDir } from "../../hooks/useCancelFilter";
import css from "./dcSections.module.css";

/**
 * DC «Отмены», третья строка (11.09, Даша через владельца: «фильтр отмен…
 * по источнику… либо сама буду вбивать имя дату время»).
 *
 * Одна полоса под вкладками, геометрия как у ряда филиалов на сетке. Свёрнута —
 * лупа и чипы; тап по лупе отдаёт всю ширину полю поиска, чипы прячутся, а
 * выбранные фильтры остаются (их видно в счётчике под рядом).
 *
 * Ввод — наш TextPad в docked-режиме: у поля inputMode="none", поэтому
 * клавиатура Android не поднимается, а текст ищет по мере набора (шит с
 * «Готово» откладывал бы поиск до конца ввода).
 */
export interface DcCancelBarProps {
  query: string; onQuery: (v: string) => void;
  typeOpts: FilterOpt[]; types: Set<string>; onTypes: (s: Set<string>) => void;
  sourceOpts: FilterOpt[]; sources: Set<string>; onSources: (s: Set<string>) => void;
  branchOpts: FilterOpt[]; branches: Set<string>; onBranches: (s: Set<string>) => void;
  sort: CancelSort; dir: CancelDir; onSort: (k: CancelSort) => void;
  activeCount: number; onReset: () => void;
  onDockHeight: (h: number) => void;
}

type DdKind = "type" | "src" | "branch";

const SORTS: { key: CancelSort; label: string }[] = [
  { key: "date", label: "Дата" },
  { key: "cancelled", label: "Отменена" },
];

export function DcCancelBar(p: DcCancelBarProps) {
  const [open, setOpen] = useState(false);      /* поле поиска развёрнуто */
  const [padOpen, setPadOpen] = useState(false);
  const [dd, setDd] = useState<{ kind: DdKind; anchor: HTMLElement } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const groups: Record<DdKind, { opts: FilterOpt[]; sel: Set<string>; set: (s: Set<string>) => void; title: string }> = {
    type: { opts: p.typeOpts, sel: p.types, set: p.onTypes, title: "Тип записи" },
    src: { opts: p.sourceOpts, sel: p.sources, set: p.onSources, title: "Источник" },
    branch: { opts: p.branchOpts, sel: p.branches, set: p.onBranches, title: "Филиал" },
  };
  const active = dd ? groups[dd.kind] : null;

  const expand = () => {
    setOpen(true);
    /* Фокус ставим после отрисовки поля — он же поднимает нашу клавиатуру. */
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const collapse = () => {
    p.onQuery(""); setOpen(false); setPadOpen(false);
    inputRef.current?.blur();
  };

  /* Тап — один пункт, долгое нажатие — несколько (общий канон дропдауна). */
  const commit = (ids: DropdownId[]) => {
    if (!active) return;
    active.set(new Set(ids.map(String)));
  };

  const chip = (kind: DdKind, label: string) => {
    const g = groups[kind];
    if (g.opts.length < 2) return null;   /* фильтр с одним значением только занимает ширину */
    return (
      <button type="button" className={`${css.tChip} ${g.sel.size ? css.tChipOn : ""}`}
        onClick={(e) => setDd({ kind, anchor: e.currentTarget })}>
        {label}{g.sel.size ? <span className={css.cxChipCount}>{g.sel.size}</span> : null} ▾
      </button>
    );
  };

  return (
    <div className={css.cxBar}>
      {open ? (
        <div className={`${css.search} ${css.cxBarSearch}`}>
          <svg className={css.lens} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" aria-hidden="true"><circle cx="6" cy="6" r="4" /><path d="M9 9l4 4" /></svg>
          <input
            ref={inputRef}
            type="text"
            className={css.searchInput}
            inputMode="none"
            data-pad-host=""
            value={p.query}
            placeholder="Имя, дата, время, телефон…"
            aria-label="Поиск по отменам"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => p.onQuery(e.target.value)}
            onFocus={() => { blurOtherField(inputRef.current); setPadOpen(true); }}
            onPointerDown={() => setPadOpen(true)}
          />
          <button type="button" className={css.searchClr} aria-label="Закрыть поиск" onClick={collapse}>
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" /></svg>
          </button>
        </div>
      ) : (
        <>
          <button type="button" className={`${css.cxLens} ${p.query ? css.cxLensOn : ""}`} aria-label="Поиск" onClick={expand}>
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" aria-hidden="true"><circle cx="6" cy="6" r="4" /><path d="M9 9l4 4" /></svg>
          </button>
          {chip("type", "Тип")}
          {chip("src", "Источник")}
          {chip("branch", "Филиал")}
          {SORTS.map((s) => (
            <button key={s.key} type="button" className={`${css.sortChip} ${p.sort === s.key ? css.sortChipOn : ""}`}
              onClick={() => p.onSort(s.key)}>
              {s.label}{p.sort === s.key ? <span className={css.sortArrow}>{p.dir === "desc" ? "↓" : "↑"}</span> : null}
            </button>
          ))}
          {p.activeCount ? (
            <button type="button" className={css.tChip} onClick={p.onReset}>Сброс</button>
          ) : null}
        </>
      )}

      <Dropdown
        open={!!dd}
        anchor={dd?.anchor ?? null}
        title={active?.title}
        filter={false}
        items={(active?.opts ?? []).map((o) => ({ id: o.id, label: o.label, meta: String(o.count) }))}
        selectedIds={[...(active?.sel ?? [])]}
        onCommit={commit}
        onClose={() => setDd(null)}
      />

      <TextPad
        open={padOpen}
        docked
        hostRef={inputRef}
        numRow
        label="Поиск по отменам"
        value={p.query}
        onChange={p.onQuery}
        onDockHeight={p.onDockHeight}
        onClose={() => setPadOpen(false)}
      />
    </div>
  );
}
